import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker, Button, Typography, Empty, Tabs, Select } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import Loading from '@/components/Loading';
import {
  useBulletinDates,
  getBulletinPdfUrl,
  type BulletinCategory,
  type BulletinEntry,
} from '@/api/weatherBulletin';

const { Text } = Typography;

const CATEGORY_TABS: { key: BulletinCategory; label: string }[] = [
  { key: 'daily', label: '每日气象' },
  { key: 'yanshen', label: '延伸期预测' },
  { key: 'monthly', label: '月气候预测' },
];

/** 从文件名中提取短标题用于显示 */
function formatEntryLabel(entry: BulletinEntry, category: BulletinCategory): string {
  if (category === 'daily') {
    const d = dayjs(entry.key, 'YYYYMMDD');
    const weekMap = ['日', '一', '二', '三', '四', '五', '六'];
    return `${d.format('YYYY-MM-DD')} 周${weekMap[d.day()]}`;
  }
  // 去掉《山东省气候预测公报》等长前缀，保留关键信息
  const name = entry.filename.replace('.pdf', '');
  return name.replace('《山东省气候预测公报》', '').replace('（', '(').replace('）', ')');
}

const DailyPanel: React.FC = () => {
  const category = 'daily';
  const { data, isLoading, error } = useBulletinDates(category);
  const entries = data?.entries ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const availableSet = useMemo(() => new Set(entries.map((e) => e.key)), [entries]);

  useEffect(() => {
    if (entries.length > 0 && !selectedKey) setSelectedKey(entries[0].key);
  }, [entries, selectedKey]);

  const selectedDayjs = selectedKey ? dayjs(selectedKey, 'YYYYMMDD') : null;

  const navigateDay = (direction: -1 | 1) => {
    if (!selectedKey) return;
    const current = dayjs(selectedKey, 'YYYYMMDD');
    let cursor = current.add(direction, 'day');
    for (let i = 0; i < 30; i++) {
      const k = cursor.format('YYYYMMDD');
      if (availableSet.has(k)) { setSelectedKey(k); return; }
      cursor = cursor.add(direction, 'day');
    }
  };

  const idx = selectedKey ? entries.findIndex((e) => e.key === selectedKey) : -1;
  const canGoPrev = idx >= 0 && idx < entries.length - 1;
  const canGoNext = idx > 0;

  if (isLoading) return <Loading />;
  if (error || !data) return <Empty description="加载失败" />;
  if (entries.length === 0) return <Empty description="暂无每日气象简报" />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Button size="small" icon={<LeftOutlined />} disabled={!canGoPrev} onClick={() => navigateDay(-1)} />
        <DatePicker
          value={selectedDayjs}
          onChange={(d) => d && setSelectedKey(d.format('YYYYMMDD'))}
          disabledDate={(d) => !availableSet.has(d.format('YYYYMMDD'))}
          allowClear={false}
          format="YYYY-MM-DD"
          style={{ width: 180 }}
        />
        <Button size="small" icon={<RightOutlined />} disabled={!canGoNext} onClick={() => navigateDay(1)} />
        {selectedKey && <Text type="secondary">{formatEntryLabel(entries.find((e) => e.key === selectedKey)!, category)}</Text>}
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 12 }}>共 {data.total} 期</Text>
      </div>
      <PdfViewer entryKey={selectedKey} category={category} />
    </div>
  );
};

const ClimatePanel: React.FC<{ category: 'yanshen' | 'monthly' }> = ({ category }) => {
  const { data, isLoading, error } = useBulletinDates(category);
  const entries = data?.entries ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (entries.length > 0 && !selectedKey) setSelectedKey(entries[0].key);
  }, [entries, selectedKey]);

  if (isLoading) return <Loading />;
  if (error || !data) return <Empty description="加载失败" />;
  if (entries.length === 0) return <Empty description="暂无数据，请确保服务端已配置抓取任务" />;

  const idx = entries.findIndex((e) => e.key === selectedKey);
  const canGoPrev = idx >= 0 && idx < entries.length - 1;
  const canGoNext = idx > 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Button
          size="small"
          icon={<LeftOutlined />}
          disabled={!canGoPrev}
          onClick={() => idx < entries.length - 1 && setSelectedKey(entries[idx + 1].key)}
        />
        <Select
          value={selectedKey}
          onChange={setSelectedKey}
          style={{ minWidth: 360 }}
          showSearch
          optionFilterProp="label"
          options={entries.map((e) => ({
            value: e.key,
            label: formatEntryLabel(e, category),
          }))}
        />
        <Button
          size="small"
          icon={<RightOutlined />}
          disabled={!canGoNext}
          onClick={() => idx > 0 && setSelectedKey(entries[idx - 1].key)}
        />
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 12 }}>共 {data.total} 期</Text>
      </div>
      <PdfViewer entryKey={selectedKey} category={category} />
    </div>
  );
};

const PdfViewer: React.FC<{ entryKey: string | null; category: BulletinCategory }> = ({ entryKey, category }) => (
  <div
    style={{
      border: '1px solid #f0f0f0',
      borderRadius: 8,
      overflow: 'hidden',
      background: '#525659',
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 260px)',
      minHeight: 500,
    }}
  >
    <div style={{ padding: '8px 16px', background: '#323639', color: '#fff', fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
      <span>{entryKey ?? '请选择'}</span>
      <span style={{ fontSize: 12, color: '#aaa' }}>{entryKey ? `${entryKey}.pdf` : ''}</span>
    </div>
    {entryKey ? (
      <iframe
        key={entryKey}
        src={getBulletinPdfUrl(entryKey, category)}
        style={{ flex: 1, border: 'none', width: '100%' }}
        title={`PDF ${entryKey}`}
      />
    ) : (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
        请选择条目查看
      </div>
    )}
  </div>
);

const WeatherBulletinPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<BulletinCategory>('daily');

  return (
    <PageContainer title="气象简报 — 数据来源：山东省气象局">
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as BulletinCategory)}
        items={CATEGORY_TABS.map((t) => ({ key: t.key, label: t.label }))}
      />
      {activeTab === 'daily' ? (
        <DailyPanel key="daily" />
      ) : (
        <ClimatePanel key={activeTab} category={activeTab} />
      )}
    </PageContainer>
  );
};

export default WeatherBulletinPage;
